import React from "react";
import {
  Box,
  Flex,
  Icon,
  Portal,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import Spinner from "@components/common/Spinner";
import { InputRadioButton } from "../InputSelect";
import { theme } from "src/styles/theme";

interface Option {
  id?: any;
  name?: any;
  idPDV?: any;
  nome?: any;
  configuracaoParcelaId?: any;
  nomeSerie?: any;
  nomeCorrespondente?: any;
  condicaoDePagamentoMatrizId?: any;
  cnpj?: any;
  descricao?: any;
  ranking?: any;
}

interface ISelect {
  title: string;
  label: string;
  error?: boolean;
  isSelected?: string;
  height?: string;
  widht?: string;
  disabled?: boolean;
  isRequirement?: boolean;
  isOportunity?: boolean;
  loading?: boolean;
  onClick?: any;
  description?: Option[];

  options: Option[];
  selectedOption: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  setId: React.Dispatch<React.SetStateAction<string>>;
}

const InputSelectWithService = ({
  title,
  label,
  error,
  isSelected,
  height,
  widht,
  disabled,
  isRequirement,
  options,
  selectedOption,
  onChange,
  onClick,
  isOportunity,
  loading,
  setId,
  description,
}: ISelect) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState<boolean>(false);
  const [check, setCheck] = React.useState("");
  const [dropdownPosition, setDropdownPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const primaryColor = useColorModeValue(theme.primary, theme.primary);
  const textColor = useColorModeValue(theme.text, theme.text);
  const borderColor = useColorModeValue(theme.disable, theme.disable);

  const handleBorderColor = () => {
    if (disabled) return `${theme.disable100}`;
    if (error) return `${theme.error}`;
    return `${theme.disable}`;
  };

  const handleBorderWidth = () => {
    if (disabled) return `2px `;
    if (error) return `3px `;
    return `2px `;
  };

  const determineId = (option: Option) =>
    option.id ||
    option.idPDV ||
    option.configuracaoParcelaId ||
    option.condicaoDePagamentoMatrizId;

  const determineName = (option: Option) =>
    option.name || option.nome || option.nomeSerie || option.nomeCorrespondente;

  const determineDescription = (option: Option) =>
    option.condicaoDePagamentoMatrizId
      ? option.condicaoDePagamentoMatrizId
      : option.descricao;

  const isChecked = (option: Option, index: number) => {
    const combinedOptionValue = `${determineName(option)} - ${determineDescription(option)}`;

    return combinedOptionValue === `${selectedOption} - ${check}`;
  };

  // Função para calcular a posição do dropdown considerando modais
  const calculateDropdownPosition = React.useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();

    // Verifica se está dentro de um modal
    const modalElement = wrapperRef.current.closest(
      '[role="dialog"], .chakra-modal__content, .modal, [data-testid="modal"]'
    );

    let calculatedLeft = rect.left;
    let calculatedTop = rect.bottom + window.scrollY;
    const calculatedWidth = rect.width;

    if (modalElement) {
      // Se está dentro de um modal, usa coordenadas relativas ao viewport
      calculatedLeft = rect.left;
      calculatedTop = rect.bottom;

      // Verifica se o dropdown vai sair da tela horizontalmente
      const viewportWidth = window.innerWidth;
      if (calculatedLeft + calculatedWidth > viewportWidth) {
        calculatedLeft = viewportWidth - calculatedWidth - 10; // 10px de margem
      }

      // Verifica se o dropdown vai sair da tela verticalmente
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 170; // altura máxima do dropdown
      if (calculatedTop + dropdownHeight > viewportHeight) {
        calculatedTop = rect.top - dropdownHeight; // posiciona acima do input
      }
    }

    setDropdownPosition({
      top: calculatedTop,
      left: Math.max(0, calculatedLeft), // garante que não seja negativo
      width: calculatedWidth,
    });
  }, []);

  // Função personalizada para gerenciar cliques fora do componente e scroll
  React.useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      // Verifica se o clique foi fora do wrapper E fora do dropdown
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }

    function handleScroll(event: Event) {
      const target = event.target as Element;

      // Verifica se o scroll está acontecendo dentro do dropdown
      if (
        dropdownRef.current &&
        (dropdownRef.current.contains(target) || dropdownRef.current === target)
      ) {
        // Se o scroll é dentro do dropdown, não fecha
        return;
      }

      // Se o scroll é fora do dropdown, fecha
      setOpen(false);
    }

    function handleResize() {
      calculateDropdownPosition();
    }

    // Adiciona um pequeno delay para evitar conflitos com o clique que abre o dropdown
    const timeoutId = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);
      // Calcula a posição inicial
      calculateDropdownPosition();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [open, calculateDropdownPosition]);

  React.useEffect(() => {
    if (isSelected) {
      setOpen(false);
    }
  }, [isSelected]);

  const handleOptionClick = (
    e: React.ChangeEvent<HTMLInputElement>,
    option: Option
  ) => {
    // Previne o fechamento imediato
    e.stopPropagation();

    // Executa a lógica de seleção
    setOpen(false);
    setCheck(
      option.condicaoDePagamentoMatrizId
        ? option.condicaoDePagamentoMatrizId
        : determineDescription(option)
    );
    setId(determineId(option));
    onChange(e);
  };

  const handleOpenDropdown = () => {
    if (disabled) return;
    calculateDropdownPosition();
    onClick();
    setOpen(!open);
  };

  return (
    <Box ref={wrapperRef} width="100%" position="relative">
      <Flex alignItems="center">
        <Text fontSize="12px" color={textColor} ml="3px">
          {isRequirement && (
            <Text as="span" color={theme.error}>
              *
            </Text>
          )}
          {title}
        </Text>
      </Flex>

      {/* Select Button */}
      <Flex
        h={isOportunity ? "34px" : "44px"}
        align="center"
        justify="space-between"
        px={3}
        cursor={disabled ? "not-allowed" : "pointer"}
        bg={"white"}
        borderRadius="8px"
        borderWidth={handleBorderWidth()}
        borderColor={handleBorderColor()}
        _focus={
          !disabled
            ? {
                borderColor: primaryColor,
                borderWidth: "1.5px",
                boxShadow: `0 0 0 1px ${primaryColor}`,
              }
            : {}
        }
        onClick={handleOpenDropdown}
      >
        <Text
          color={disabled ? theme.text50 : theme.text}
          fontWeight={isSelected ? "bold" : "normal"}
          fontSize={label.length > 20 ? "12px" : "14px"}
          isTruncated
          flex={1}
        >
          {label}
        </Text>

        <Flex
          w={"fit-content"}
          direction="column"
          align="flex-end"
          justify="center"
        >
          <Icon as={IoIosArrowUp} color={primaryColor} fontSize="sm" />
          <Icon as={IoIosArrowDown} color={primaryColor} fontSize="sm" />
        </Flex>
      </Flex>

      {open && (
        <Portal>
          <Box
            ref={dropdownRef}
            position="fixed"
            maxHeight={height || "170px"}
            minHeight="50px"
            bg="white"
            borderRadius="8px"
            mt="1"
            border="1px solid"
            borderColor={borderColor}
            boxShadow="xl"
            zIndex={99999}
            overflowY="auto"
            left={`${dropdownPosition.left}px`}
            top={`${dropdownPosition.top}px`}
            width={`${dropdownPosition.width}px`}
            // Previne que cliques dentro do dropdown sejam propagados
            onClick={(e) => e.stopPropagation()}
            // Previne que o scroll dentro do dropdown seja propagado
            onScroll={(e) => e.stopPropagation()}
          >
            {loading ? (
              <Flex
                justifyContent="center"
                alignItems="center"
                py="4"
                minHeight="50px"
              >
                <Spinner />
              </Flex>
            ) : (
              <Box as="ul" p="2" m="0" listStyleType="none">
                {options.map((option, index) => (
                  <InputRadioButton
                    key={determineId(option) || index}
                    id={determineId(option)}
                    name={determineName(option)}
                    checked={isChecked(option, index)}
                    onChange={(e) => handleOptionClick(e, option)}
                    description={
                      option.condicaoDePagamentoMatrizId
                        ? option.descricao
                        : determineDescription(option)
                    }
                    ranking={option.ranking}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Portal>
      )}
    </Box>
  );
};
export default InputSelectWithService;
